import { contactInfo } from './clubInfo';

export const SITE_NAME = 'Chantilly Club';
export const SITE_URL =
  process.env.REACT_APP_SITE_URL || 'https://chantillyclub.eu';

const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const DEFAULT_KEYWORDS = [
  'Chantilly Club',
  'Chantilly Club Villeneuve-d\'Ascq',
  'Chantilly Club padel',
  'padel Villeneuve-d\'Ascq',
  'padel Lille',
  'meilleur club padel Lille',
  'complexe sportif Villeneuve-d\'Ascq',
  'fitness Villeneuve-d\'Ascq',
  'loisirs Lille métropole',
  'flippers Villeneuve-d\'Ascq',
  'badminton Lille',
  'basket Villeneuve-d\'Ascq',
  'restaurant sportif Lille',
  'réservation terrain padel Lille',
  'licence FFT padel Chantilly',
].join(', ');

export const getOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['SportsActivityLocation', 'LocalBusiness', 'SportsClub'],
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      alternateName: [
        'Le Chantilly Club',
        'Chantilly Club Villeneuve-d\'Ascq',
        'Chantilly Club padel',
        'Club de padel Chantilly',
      ],
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
      image: DEFAULT_IMAGE,
      description:
        'Chantilly Club, complexe multisports premium à Villeneuve-d\'Ascq près de Lille : 11 terrains de padel, fitness, badminton, basket, 20 flippers et restaurant. Réservation en ligne.',
      telephone: contactInfo.phones.main,
      email: contactInfo.emails.general,
      address: {
        '@type': 'PostalAddress',
        streetAddress: contactInfo.address.street,
        addressLocality: 'Villeneuve-d\'Ascq',
        postalCode: '59491',
        addressRegion: 'Hauts-de-France',
        addressCountry: 'FR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 50.6233,
        longitude: 3.1442,
      },
      areaServed: [
        { '@type': 'City', name: 'Villeneuve-d\'Ascq' },
        { '@type': 'City', name: 'Lille' },
        { '@type': 'AdministrativeArea', name: 'Métropole Européenne de Lille' },
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '00:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday', 'Sunday'],
          opens: '09:00',
          closes: '20:00',
        },
      ],
      sameAs: [
        'https://playtomic.com/clubs/le-chantilly-club',
      ],
      priceRange: '€€',
      amenityFeature: [
        { '@type': 'LocationFeatureSpecification', name: '11 terrains de padel indoor & outdoor' },
        { '@type': 'LocationFeatureSpecification', name: 'Salle de fitness' },
        { '@type': 'LocationFeatureSpecification', name: 'Arcade et flippers' },
        { '@type': 'LocationFeatureSpecification', name: 'Restaurant' },
        { '@type': 'LocationFeatureSpecification', name: 'Parking gratuit' },
      ],
      knowsAbout: [
        'Padel',
        'Fitness',
        'Badminton',
        'Basketball',
        'Flippers',
        'Tournois FFT',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      alternateName: 'Chantilly Club padel Lille',
      description:
        'Site officiel du Chantilly Club — padel, fitness et loisirs à Villeneuve-d\'Ascq (Lille métropole)',
      publisher: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'fr-FR',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/contact?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ],
});

const pageSeo = {
  '/': {
    title: 'Chantilly Club | Padel & Complexe Sportif n°1 Villeneuve-d\'Ascq — Lille',
    description:
      'Chantilly Club, le complexe multisports de référence à Villeneuve-d\'Ascq (Lille) : 11 terrains de padel, fitness, badminton, basket, flippers et restaurant. Réservez en ligne dès maintenant.',
    keywords: DEFAULT_KEYWORDS,
  },
  '/padel': {
    title: 'Padel Villeneuve-d\'Ascq & Lille | Chantilly Club — 11 Terrains',
    description:
      'Réservez un terrain de padel au Chantilly Club à Villeneuve-d\'Ascq. 11 terrains indoor & outdoor, coaching pro, tournois FFT homologués. Le padel près de Lille.',
    keywords:
      'padel Villeneuve-d\'Ascq, padel Lille, Chantilly Club padel, réservation padel Lille, terrain padel 59, club padel métropole lilloise',
  },
  '/proshop': {
    title: 'Proshop Padel | Chantilly Club Villeneuve-d\'Ascq',
    description:
      'Achetez raquettes, balles et équipement padel au Proshop du Chantilly Club. Retrait au club, paiement sécurisé.',
    keywords: 'proshop padel Chantilly Club, raquette padel Villeneuve-d\'Ascq, équipement padel Lille',
  },
  '/licences': {
    title: 'Licence FFT Padel & Adhésion Club | Chantilly Club Villeneuve-d\'Ascq',
    description:
      'Prenez votre licence FFT padel et votre adhésion au Chantilly Club en ligne. Paiement sécurisé, saison sportive Villeneuve-d\'Ascq / Lille.',
    keywords:
      'licence FFT padel, adhésion Chantilly Club, licence padel Villeneuve-d\'Ascq, licence FFT Lille',
  },
  '/fitness': {
    title: 'Fitness Chantilly Club | Salle de Sport Villeneuve-d\'Ascq',
    description:
      'Salle de fitness au Chantilly Club : équipements premium, cabines privées et abonnement en ligne à Villeneuve-d\'Ascq près de Lille.',
    keywords: 'fitness Chantilly Club, salle de sport Villeneuve-d\'Ascq, gym Lille',
  },
  '/loisirs': {
    title: 'Loisirs & Flippers | Chantilly Club Villeneuve-d\'Ascq',
    description:
      '20 flippers premium, pétanque, billard et baby-foot au Chantilly Club. 2500 m² de divertissement à Villeneuve-d\'Ascq.',
    keywords: 'flippers Chantilly Club, loisirs Villeneuve-d\'Ascq, arcade Lille',
  },
  '/badminton': {
    title: 'Badminton | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Terrains de badminton au Chantilly Club. Complexe sportif à Villeneuve-d\'Ascq près de Lille.',
    keywords: 'badminton Chantilly Club, badminton Villeneuve-d\'Ascq',
  },
  '/basket': {
    title: 'Basketball | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Terrains de basket au Chantilly Club. Sport et convivialité à Villeneuve-d\'Ascq.',
    keywords: 'basket Chantilly Club, basketball Villeneuve-d\'Ascq',
  },
  '/arcade': {
    title: 'Arcade & Jeux | Chantilly Club',
    description: 'Borne arcade et jeux rétro au Chantilly Club à Villeneuve-d\'Ascq.',
    keywords: 'arcade Chantilly Club, jeux Villeneuve-d\'Ascq',
  },
  '/tournois': {
    title: 'Tournois | Chantilly Club — Padel & Flippers',
    description: '+200 tournois homologués par saison au Chantilly Club : padel et flippers à Villeneuve-d\'Ascq.',
    keywords: 'tournois padel Chantilly Club, tournois flippers Lille',
  },
  '/tournois-padel': {
    title: 'Tournois Padel | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Inscrivez-vous aux tournois de padel homologués du Chantilly Club via JA Padel. Paiement sur place le jour du tournoi.',
    keywords: 'tournoi padel Chantilly Club, compétition padel Lille',
  },
  '/tournois-flippers': {
    title: 'Tournois Flippers | Chantilly Club',
    description: 'Tournois de flippers au Chantilly Club. Collection premium à Villeneuve-d\'Ascq.',
    keywords: 'tournoi flippers Chantilly Club, pinball Lille',
  },
  '/restaurant': {
    title: 'Commande en ligne restauration | Chantilly Club',
    description: 'Commandez pizzas, menus et desserts en ligne au Chantilly Club. Retrait au club, paiement sécurisé.',
    keywords: 'commande en ligne restaurant Chantilly Club, snacking Villeneuve-d\'Ascq, retrait club',
  },
  '/actualites': {
    title: 'Actualités | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Les news du Chantilly Club : événements, nouveautés sport et restauration à Villeneuve-d\'Ascq.',
    keywords: 'actualités Chantilly Club, news padel Lille',
  },
  '/entreprises': {
    title: 'Entreprises & Événements | Chantilly Club',
    description: 'Organisez vos événements d\'entreprise au Chantilly Club : séminaires, team building, privatisation.',
    keywords: 'événement entreprise Chantilly Club, séminaire Villeneuve-d\'Ascq',
  },
  '/seminaires': {
    title: 'Séminaires | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Salles de réunion et séminaires au Chantilly Club avec activités sportives sur place.',
    keywords: 'séminaire Chantilly Club, salle réunion Villeneuve-d\'Ascq',
  },
  '/team-building': {
    title: 'Team Building | Chantilly Club',
    description: 'Team building sportif au Chantilly Club : padel, fitness, arcade à Villeneuve-d\'Ascq.',
    keywords: 'team building Chantilly Club, activité entreprise Lille',
  },
  '/privatisation': {
    title: 'Privatisation | Chantilly Club Villeneuve-d\'Ascq',
    description: 'Privatisez le Chantilly Club pour vos événements : anniversaires, soirées, entreprises.',
    keywords: 'privatisation Chantilly Club, location complexe sportif Lille',
  },
  '/contact': {
    title: 'Contact & Accès | Chantilly Club — 1 Rue Hoche, Villeneuve-d\'Ascq',
    description:
      'Contactez le Chantilly Club : 1 Rue Hoche, 59491 Villeneuve-d\'Ascq. Horaires, parking gratuit, téléphone et email. Club padel près de Lille.',
    keywords: 'contact Chantilly Club, adresse Chantilly Club, horaires Chantilly Club, padel Villeneuve-d\'Ascq adresse',
  },
  '/cgv': {
    title: 'CGV | Chantilly Club',
    description: 'Conditions générales de vente du Chantilly Club à Villeneuve-d\'Ascq.',
    keywords: 'CGV Chantilly Club',
  },
  '/mentions-legales': {
    title: 'Mentions légales | Chantilly Club',
    description: 'Mentions légales du site officiel du Chantilly Club.',
    keywords: 'mentions légales Chantilly Club',
  },
  '/politique-confidentialite': {
    title: 'Politique de confidentialité | Chantilly Club',
    description: 'Politique de confidentialité et protection des données du Chantilly Club.',
    keywords: 'confidentialité Chantilly Club, RGPD',
  },
};

const defaultSeo = {
  title: `${SITE_NAME} | Complexe Sportif Villeneuve-d\'Ascq`,
  description: 'Chantilly Club — complexe multisports à Villeneuve-d\'Ascq : padel, fitness, loisirs et restaurant.',
  keywords: DEFAULT_KEYWORDS,
};

export const getSeoForPath = (pathname) => {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const seo = pageSeo[normalized] || defaultSeo;

  return {
    ...seo,
    image: DEFAULT_IMAGE,
    jsonLd: getOrganizationJsonLd(),
  };
};

export const PUBLIC_ROUTES = Object.keys(pageSeo);
